import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  IconButton,
  Tooltip,
  Slider,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import InfoIcon from '@mui/icons-material/Info';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import LanguageIcon from '@mui/icons-material/Language';
import AppsIcon from '@mui/icons-material/Apps';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import HubIcon from '@mui/icons-material/Hub';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip as RechartsTooltip } from 'recharts';

// Node and Link interfaces (matching NetworkGraph)
interface Node {
  id: string;
  type?: string;
  group?: number;
  label?: string;
  entity_type?: string;
  place_name?: string;
  place_type?: string;
  place_address?: string;
  domain?: string;
  sample_url?: string;
  app_name?: string;
  package_name?: string;
  x?: number;
  y?: number;
}

interface Link {
  source: string | Node;
  target: string | Node;
  weight?: number;
  type?: string;
  comms_direction?: string;
  timestamp?: string;
}

// Color map for node and link types - matches NetworkGraph
// Military/Operational color palette - tactical, analytical look
const typeColorMap: Record<string, string> = {
  call: '#3D5A80',      // Navy (primary comms)
  sms: '#98C1D9',       // Cold blue (text comms)
  browsing: '#415A77',  // Gunmetal (digital activity)
  app: '#D6A86C',       // Sand (app usage)
  dwell: '#EE6C4D',     // Burnt orange (location activity)
  device: '#4A7C59',    // Military green (entities/devices)
  phone: '#3D5A80',     // Navy (external numbers)
  location: '#EE6C4D',  // Burnt orange (places)
  website: '#98C1D9',   // Cold blue (web domains)
};

// Depth level colors - military operational
const DEPTH_COLORS = {
  seed: '#4A7C59',  // Military green (center/command)
  l1: '#3D5A80',    // Navy (direct connections)
  l2: '#98C1D9',    // Cold blue (2 hops)
  l3: '#415A77',    // Gunmetal (3 hops)
  l4: '#EE6C4D',    // Burnt orange (4 hops - highlight)
};

// Ring colors (subtle guides) - muted military tones
const RING_COLORS = [
  'rgba(74, 124, 89, 0.35)',    // L0 - Military green (seed)
  'rgba(61, 90, 128, 0.3)',     // L1 - Navy
  'rgba(152, 193, 217, 0.25)',  // L2 - Cold blue
  'rgba(65, 90, 119, 0.2)',     // L3 - Gunmetal
  'rgba(238, 108, 77, 0.15)',   // L4 - Burnt orange
];

interface EgoRadialGraphProps {
  nodes: Node[];
  links: Link[];
  selectedEntityIds: string[];
  nodeDepths: Map<string, number>;
  depthLevel: number | 'all';
  onNodeClick?: (node: Node) => void;
  onToggleExpand?: (nodeId: string) => void;
  onToggleHide?: (nodeId: string) => void;
  onEntityFilter?: (entityIds: string[]) => void;
  onViewDetails?: (node: Node, stats: any) => void;
  expandedNodes?: Set<string>;
}

const EgoRadialGraph: React.FC<EgoRadialGraphProps> = ({
  nodes,
  links,
  selectedEntityIds,
  nodeDepths,
  depthLevel,
  onNodeClick,
  onToggleExpand,
  onToggleHide,
  onEntityFilter,
  onViewDetails,
  expandedNodes = new Set(),
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const rechartsTooltipProps = useMemo(
    () => ({
      contentStyle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        fontSize: '0.75rem',
      },
      labelStyle: { color: theme.palette.text.primary },
      itemStyle: { color: theme.palette.text.secondary },
    }),
    [theme],
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // View state
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: Node } | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [nodeSize, setNodeSize] = useState(4);
  const [selectedConnectionType, setSelectedConnectionType] = useState<string>('all');
  
  // Focus state - click a node to highlight its connections
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);

  // Connection type options
  const connectionTypes = [
    { value: 'all', label: 'All', icon: null },
    { value: 'call', label: 'Calls', icon: <PhoneIcon sx={{ fontSize: 14 }} /> },
    { value: 'sms', label: 'SMS', icon: <SmsIcon sx={{ fontSize: 14 }} /> },
    { value: 'browsing', label: 'Web', icon: <LanguageIcon sx={{ fontSize: 14 }} /> },
    { value: 'app', label: 'Apps', icon: <AppsIcon sx={{ fontSize: 14 }} /> },
    { value: 'dwell', label: 'Dwell', icon: <LocationOnIcon sx={{ fontSize: 14 }} /> },
  ];

  // Filter links by selected connection type
  const filteredLinks = useMemo(() => {
    if (selectedConnectionType === 'all') return links;
    return links.filter(link => link.type === selectedConnectionType);
  }, [links, selectedConnectionType]);

  // Get nodes that are connected via filtered links
  const connectedNodeIds = useMemo(() => {
    const ids = new Set<string>();
    // Always include seed nodes
    selectedEntityIds.forEach(id => ids.add(id));
    // Add nodes connected via filtered links
    filteredLinks.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      ids.add(sourceId);
      ids.add(targetId);
    });
    return ids;
  }, [filteredLinks, selectedEntityIds]);

  // Filter nodes to only those connected via filtered links
  const filteredNodes = useMemo(() => {
    if (selectedConnectionType === 'all') return nodes;
    return nodes.filter(node => connectedNodeIds.has(node.id));
  }, [nodes, connectedNodeIds, selectedConnectionType]);

  // Calculate depth distribution for chart
  const depthDistribution = useMemo(() => {
    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    filteredNodes.forEach(node => {
      const depth = nodeDepths.get(node.id);
      if (depth !== undefined && depth >= 0 && depth <= 4) {
        counts[depth]++;
      }
    });
    return [
      { level: 'Seed', count: counts[0], depth: 0, color: DEPTH_COLORS.seed },
      { level: 'L1', count: counts[1], depth: 1, color: DEPTH_COLORS.l1 },
      { level: 'L2', count: counts[2], depth: 2, color: DEPTH_COLORS.l2 },
      { level: 'L3', count: counts[3], depth: 3, color: DEPTH_COLORS.l3 },
      { level: 'L4', count: counts[4], depth: 4, color: DEPTH_COLORS.l4 },
    ].filter(d => d.count > 0);
  }, [filteredNodes, nodeDepths]);

  // Connection type counts for badges
  const connectionTypeCounts = useMemo(() => {
    const counts: Record<string, number> = { call: 0, sms: 0, browsing: 0, app: 0, dwell: 0 };
    links.forEach(link => {
      if (link.type && counts[link.type] !== undefined) {
        counts[link.type]++;
      }
    });
    return counts;
  }, [links]);

  // Calculate which nodes and links are connected to the focused node
  // For radial graph: trace the SHORTEST PATH back to seed using BFS on filtered links
  const focusedConnections = useMemo(() => {
    if (!focusedNodeId) {
      return { connectedNodeIds: new Set<string>(), connectedLinkKeys: new Set<string>(), pathFound: true };
    }
    
    const connectedNodeIds = new Set<string>();
    const connectedLinkKeys = new Set<string>();
    
    // The focused node itself is always "connected"
    connectedNodeIds.add(focusedNodeId);
    
    // Check if focused node is a seed
    const isFocusedSeed = selectedEntityIds.includes(focusedNodeId);
    
    if (isFocusedSeed) {
      // If focused node is the seed, show its direct connections
      filteredLinks.forEach((link, index) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        
        if (sourceId === focusedNodeId || targetId === focusedNodeId) {
          connectedNodeIds.add(sourceId);
          connectedNodeIds.add(targetId);
          connectedLinkKeys.add(`${index}`);
        }
      });
      return { connectedNodeIds, connectedLinkKeys, pathFound: true };
    }
    
    // Build adjacency list for BFS
    const adjacency = new Map<string, Set<string>>();
    const linkLookup = new Map<string, number[]>();
    
    filteredLinks.forEach((link, index) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      
      if (!adjacency.has(sourceId)) adjacency.set(sourceId, new Set());
      if (!adjacency.has(targetId)) adjacency.set(targetId, new Set());
      adjacency.get(sourceId)!.add(targetId);
      adjacency.get(targetId)!.add(sourceId);
      
      const key1 = `${sourceId}-${targetId}`;
      const key2 = `${targetId}-${sourceId}`;
      if (!linkLookup.has(key1)) linkLookup.set(key1, []);
      if (!linkLookup.has(key2)) linkLookup.set(key2, []);
      linkLookup.get(key1)!.push(index);
      linkLookup.get(key2)!.push(index);
    });
    
    // BFS from focused node to find shortest path to ANY seed
    const visited = new Set<string>([focusedNodeId]);
    const parent = new Map<string, string>();
    const queue: string[] = [focusedNodeId];
    let foundSeed: string | null = null;
    
    while (queue.length > 0 && !foundSeed) {
      const current = queue.shift()!;
      const neighbors = adjacency.get(current);
      if (!neighbors) continue;
      
      for (const neighborId of Array.from(neighbors)) {
        if (visited.has(neighborId)) continue;
        visited.add(neighborId);
        parent.set(neighborId, current);
        
        if (selectedEntityIds.includes(neighborId)) {
          foundSeed = neighborId;
          break;
        }
        queue.push(neighborId);
      }
    }
    
    // Reconstruct path if found
    if (foundSeed) {
      let current = foundSeed;
      while (current !== focusedNodeId) {
        connectedNodeIds.add(current);
        const parentNode = parent.get(current);
        if (!parentNode) break;
        
        const linkKey = `${current}-${parentNode}`;
        const linkIndices = linkLookup.get(linkKey) || [];
        linkIndices.forEach(idx => connectedLinkKeys.add(`${idx}`));
        current = parentNode;
      }
    }
    
    return { connectedNodeIds, connectedLinkKeys, pathFound: foundSeed !== null };
  }, [focusedNodeId, filteredLinks, selectedEntityIds]);

  // DISCOVERY MODE: Calculate entity rankings by connection depth
  // This runs a BFS from each device node to count connections at each level
  const entityRankings = useMemo(() => {
    // Only calculate if no seed is selected (discovery mode)
    if (selectedEntityIds.length > 0) return [];

    // Build adjacency list
    const adjacency = new Map<string, Set<string>>();
    filteredLinks.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      
      if (!adjacency.has(sourceId)) adjacency.set(sourceId, new Set());
      if (!adjacency.has(targetId)) adjacency.set(targetId, new Set());
      adjacency.get(sourceId)!.add(targetId);
      adjacency.get(targetId)!.add(sourceId);
    });

    // Get device/entity nodes only (not locations, websites, phones)
    const deviceNodes = nodes.filter(n => n.type === 'device' || n.entity_type);
    
    // Calculate depth counts for each device node via BFS
    const rankings: Array<{
      id: string;
      label: string;
      type: string;
      l1: number;
      l2: number;
      l3: number;
      l4: number;
      total: number;
      typeBreakdown: Record<string, number>;
    }> = [];

    deviceNodes.forEach(device => {
      const visited = new Set<string>();
      const depthCounts = { l1: 0, l2: 0, l3: 0, l4: 0 };
      const typeBreakdown: Record<string, number> = { call: 0, sms: 0, browsing: 0, app: 0, dwell: 0 };
      const queue: Array<{ nodeId: string; depth: number }> = [];
      
      // Start BFS from this device
      visited.add(device.id);
      queue.push({ nodeId: device.id, depth: 0 });
      
      while (queue.length > 0) {
        const { nodeId, depth } = queue.shift()!;
        const neighbors = adjacency.get(nodeId);
        if (!neighbors) continue;
        
        neighbors.forEach(neighborId => {
          if (!visited.has(neighborId)) {
            visited.add(neighborId);
            const newDepth = depth + 1;
            
            if (newDepth === 1) depthCounts.l1++;
            else if (newDepth === 2) depthCounts.l2++;
            else if (newDepth === 3) depthCounts.l3++;
            else if (newDepth === 4) depthCounts.l4++;
            
            if (newDepth < 4) {
              queue.push({ nodeId: neighborId, depth: newDepth });
            }
          }
        });
      }

      // Count connection types for this device
      filteredLinks.forEach(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;
        if ((sourceId === device.id || targetId === device.id) && link.type) {
          typeBreakdown[link.type] = (typeBreakdown[link.type] || 0) + 1;
        }
      });

      const total = depthCounts.l1 + depthCounts.l2 + depthCounts.l3 + depthCounts.l4;
      if (total > 0) {
        rankings.push({
          id: device.id,
          label: device.label || device.id,
          type: device.entity_type || device.type || 'device',
          ...depthCounts,
          total,
          typeBreakdown,
        });
      }
    });

    // Sort by total connections descending - show top 50
    return rankings.sort((a, b) => b.total - a.total).slice(0, 50);
  }, [nodes, filteredLinks, selectedEntityIds]);

  // Hovered entity in discovery mode
  const [hoveredEntity, setHoveredEntity] = useState<string | null>(null);

  // Calculate node positions in radial layout
  const nodePositions = useMemo(() => {
    const positions = new Map<string, { x: number; y: number; depth: number }>();
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    
    // Group nodes by depth (use filtered nodes)
    const nodesByDepth = new Map<number, Node[]>();
    filteredNodes.forEach(node => {
      const depth = nodeDepths.get(node.id) ?? -1;
      if (depth < 0) return; // Skip nodes without depth
      
      if (!nodesByDepth.has(depth)) {
        nodesByDepth.set(depth, []);
      }
      nodesByDepth.get(depth)!.push(node);
    });
    
    // Calculate ring radius based on available space
    const maxDepth = typeof depthLevel === 'number' ? depthLevel : 4;
    const minRadius = 60;
    const maxRadius = Math.min(dimensions.width, dimensions.height) / 2 - 80;
    const ringSpacing = (maxRadius - minRadius) / Math.max(maxDepth, 1);
    
    // Position nodes on each ring
    nodesByDepth.forEach((ringNodes, depth) => {
      if (depth > maxDepth) return;
      
      const radius = depth === 0 ? 0 : minRadius + (depth - 1) * ringSpacing + ringSpacing;
      
      // Sort nodes by type for grouping
      ringNodes.sort((a, b) => (a.type || '').localeCompare(b.type || ''));
      
      // Distribute nodes around the ring
      const angleStep = (2 * Math.PI) / Math.max(ringNodes.length, 1);
      const startAngle = -Math.PI / 2; // Start from top
      
      ringNodes.forEach((node, index) => {
        const angle = startAngle + index * angleStep;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        positions.set(node.id, { x, y, depth });
      });
    });
    
    return positions;
  }, [filteredNodes, nodeDepths, depthLevel, dimensions]);

  // Calculate link data with positions
  const linkData = useMemo(() => {
    return filteredLinks.map((link, index) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      const sourcePos = nodePositions.get(sourceId);
      const targetPos = nodePositions.get(targetId);
      
      if (!sourcePos || !targetPos) return null;
      
      return {
        ...link,
        sourceId,
        targetId,
        sourcePos,
        targetPos,
        linkIndex: index, // Track original index for path highlighting
      };
    }).filter(Boolean);
  }, [filteredLinks, nodePositions]);

  // Node degree calculation (based on filtered links)
  const nodeDegrees = useMemo(() => {
    const degrees = new Map<string, number>();
    filteredLinks.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      degrees.set(sourceId, (degrees.get(sourceId) || 0) + 1);
      degrees.set(targetId, (degrees.get(targetId) || 0) + 1);
    });
    return degrees;
  }, [filteredLinks]);

  // Reset focus when selected entities change
  useEffect(() => {
    setFocusedNodeId(null);
  }, [selectedEntityIds]);

  // Handle Escape key to clear focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedNodeId) {
        setFocusedNodeId(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedNodeId]);

  // Handle container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Find node at position
  const findNodeAtPosition = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left - pan.x) / zoom;
    const y = (clientY - rect.top - pan.y) / zoom;

    for (const node of filteredNodes) {
      const pos = nodePositions.get(node.id);
      if (!pos) continue;

      const dx = x - pos.x;
      const dy = y - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const hitRadius = (nodeSize + 4) / zoom;

      if (dist <= hitRadius) {
        return node;
      }
    }
    return null;
  }, [filteredNodes, nodePositions, nodeSize, zoom, pan]);

  // Mouse handlers
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Always track mouse position for tooltip
    setMousePosition({ x: e.clientX, y: e.clientY });
    
    if (isDragging) {
      setPan(prev => ({
        x: prev.x + e.movementX,
        y: prev.y + e.movementY,
      }));
      return;
    }

    const node = findNodeAtPosition(e.clientX, e.clientY);
    setHoveredNode(node);
  }, [isDragging, findNodeAtPosition]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Left click
      const node = findNodeAtPosition(e.clientX, e.clientY);
      if (!node) {
        setIsDragging(true);
      }
    }
  }, [findNodeAtPosition]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      return;
    }

    if (e.button === 0) { // Left click
      const node = findNodeAtPosition(e.clientX, e.clientY);
      if (node) {
        // Toggle focus: click same node to unfocus, or click new node to focus it
        setFocusedNodeId(prev => prev === node.id ? null : node.id);
        if (onNodeClick) {
          onNodeClick(node);
        }
      } else {
        // Clicked on empty space - clear focus
        setFocusedNodeId(null);
      }
    }
  }, [isDragging, findNodeAtPosition, onNodeClick]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const node = findNodeAtPosition(e.clientX, e.clientY);
    if (node) {
      setContextMenu({ x: e.clientX, y: e.clientY, node });
    }
  }, [findNodeAtPosition]);

  const zoomAtCanvasPoint = useCallback((canvasX: number, canvasY: number, zoomFactor: number) => {
    setZoom(prevZoom => {
      const nextZoom = Math.max(0.1, Math.min(5, prevZoom * zoomFactor));
      if (nextZoom === prevZoom) return prevZoom;

      setPan(prevPan => {
        const graphX = (canvasX - prevPan.x) / prevZoom;
        const graphY = (canvasY - prevPan.y) / prevZoom;

        return {
          x: canvasX - graphX * nextZoom,
          y: canvasY - graphY * nextZoom,
        };
      });

      return nextZoom;
    });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAtCanvasPoint(canvasX, canvasY, zoomFactor);
  }, [zoomAtCanvasPoint]);

  // Draw the graph
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = dimensions.width * window.devicePixelRatio;
    canvas.height = dimensions.height * window.devicePixelRatio;
    canvas.style.width = `${dimensions.width}px`;
    canvas.style.height = `${dimensions.height}px`;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.fillStyle = isDarkMode ? '#1a1a2e' : '#fafafa';
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    // Apply zoom and pan
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const maxDepth = typeof depthLevel === 'number' ? depthLevel : 4;
    const minRadius = 60;
    const maxRadius = Math.min(dimensions.width, dimensions.height) / 2 - 80;
    const ringSpacing = (maxRadius - minRadius) / Math.max(maxDepth, 1);

    // Draw ring guides
    for (let d = 1; d <= maxDepth; d++) {
      const radius = minRadius + (d - 1) * ringSpacing + ringSpacing;
      
      // Ring circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = RING_COLORS[d] || 'rgba(128, 128, 128, 0.1)';
      ctx.lineWidth = 2 / zoom;
      ctx.setLineDash([5 / zoom, 5 / zoom]);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Ring label
      ctx.font = `${12 / zoom}px Arial`;
      ctx.fillStyle = isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)';
      ctx.textAlign = 'center';
      ctx.fillText(`L${d}`, centerX, centerY - radius - 8 / zoom);
    }

    // Draw links (curved)
    const hasFocus = focusedNodeId !== null;
    linkData.forEach((link: any) => {
      if (!link) return;
      
      const { sourcePos, targetPos, type, linkIndex } = link;
      const color = typeColorMap[type || ''] || '#666';
      
      // Check if this link is part of the path to focused node (uses traced path, not just direct connections)
      const isConnectedToFocus = hasFocus && focusedConnections.connectedLinkKeys.has(`${linkIndex}`);
      
      // Calculate control point for curve
      const midX = (sourcePos.x + targetPos.x) / 2;
      const midY = (sourcePos.y + targetPos.y) / 2;
      
      // Pull control point toward center for cross-ring connections
      const depthDiff = Math.abs(sourcePos.depth - targetPos.depth);
      const curveFactor = depthDiff > 0 ? 0.3 : 0.1;
      const controlX = midX + (centerX - midX) * curveFactor;
      const controlY = midY + (centerY - midY) * curveFactor;
      
      ctx.beginPath();
      ctx.moveTo(sourcePos.x, sourcePos.y);
      ctx.quadraticCurveTo(controlX, controlY, targetPos.x, targetPos.y);
      ctx.strokeStyle = color;
      // Apply focus-based opacity: highlighted links are brighter, others fade
      ctx.globalAlpha = hasFocus 
        ? (isConnectedToFocus ? 0.9 : 0.05)  // Focused mode: highlight connected, fade others
        : 0.4;  // Normal mode
      ctx.lineWidth = (hasFocus && isConnectedToFocus ? 2 : 1) / zoom;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    // Draw nodes
    filteredNodes.forEach(node => {
      const pos = nodePositions.get(node.id);
      if (!pos) return;

      const isSeed = selectedEntityIds.includes(node.id);
      const isHovered = hoveredNode?.id === node.id;
      const isExpanded = expandedNodes.has(node.id);
      const isFocused = focusedNodeId === node.id;
      const isConnectedToFocus = focusedConnections.connectedNodeIds.has(node.id);
      const degree = nodeDegrees.get(node.id) || 0;
      
      // Size based on degree and seed status
      let size = nodeSize + Math.min(degree / 10, 4);
      if (isSeed) size *= 1.5;
      if (isHovered || isFocused) size *= 1.2;

      // Get color
      const color = typeColorMap[node.type || ''] || '#888';
      
      // Calculate opacity based on focus state
      const nodeOpacity = hasFocus 
        ? (isConnectedToFocus ? 1 : 0.1)  // Focused mode: highlight connected, fade others
        : 1;  // Normal mode

      // Draw expanded indicator
      if (isExpanded && nodeOpacity > 0.5) {
        ctx.globalAlpha = nodeOpacity;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size + 6 / zoom, 0, 2 * Math.PI);
        ctx.strokeStyle = '#00ff88';
        ctx.lineWidth = 2 / zoom;
        ctx.stroke();
      }

      // Draw seed indicator (gold ring)
      if (isSeed && nodeOpacity > 0.5) {
        ctx.globalAlpha = nodeOpacity;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size + 4 / zoom, 0, 2 * Math.PI);
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 3 / zoom;
        ctx.stroke();
      }
      
      // Draw focus indicator (cyan glow ring)
      if (isFocused) {
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size + 8 / zoom, 0, 2 * Math.PI);
        ctx.strokeStyle = '#00ffff';
        ctx.lineWidth = 3 / zoom;
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 15 / zoom;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Draw hover highlight
      if (isHovered && !isFocused) {
        ctx.globalAlpha = nodeOpacity;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size + 2 / zoom, 0, 2 * Math.PI);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2 / zoom;
        ctx.stroke();
      }

      // Draw node
      ctx.globalAlpha = nodeOpacity;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, size, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.globalAlpha = 1;

      // Draw label (show for focused connections even if labels are off)
      const shouldShowLabel = showLabels || isHovered || isSeed || (hasFocus && isConnectedToFocus);
      if (shouldShowLabel && nodeOpacity > 0.5) {
        const label = node.label || node.id;
        const displayLabel = label.length > 15 ? label.slice(0, 12) + '...' : label;
        
        ctx.font = `${(isSeed || isHovered || isFocused ? 11 : 9) / zoom}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        // Background for label
        const textWidth = ctx.measureText(displayLabel).width;
        ctx.fillStyle = isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)';
        ctx.fillRect(
          pos.x - textWidth / 2 - 2 / zoom,
          pos.y + size + 2 / zoom,
          textWidth + 4 / zoom,
          12 / zoom
        );
        
        ctx.fillStyle = isDarkMode ? '#fff' : '#000';
        ctx.fillText(displayLabel, pos.x, pos.y + size + 3 / zoom);
      }
    });

    ctx.restore();

  }, [filteredNodes, filteredLinks, nodePositions, linkData, nodeDepths, nodeDegrees, selectedEntityIds, 
      depthLevel, dimensions, zoom, pan, isDarkMode, hoveredNode, showLabels, nodeSize, expandedNodes,
      focusedNodeId, focusedConnections]);

  // Context menu handlers
  const handleCloseContextMenu = () => setContextMenu(null);

  const handleViewDetails = () => {
    if (contextMenu && onViewDetails) {
      const node = contextMenu.node;
      const connections = links.filter(l => {
        const sid = typeof l.source === 'string' ? l.source : l.source.id;
        const tid = typeof l.target === 'string' ? l.target : l.target.id;
        return sid === node.id || tid === node.id;
      });
      
      onViewDetails(node, {
        totalConnections: connections.length,
        callConnections: connections.filter(l => l.type === 'call').length,
        smsConnections: connections.filter(l => l.type === 'sms').length,
        browsingConnections: connections.filter(l => l.type === 'browsing').length,
        appConnections: connections.filter(l => l.type === 'app').length,
        dwellConnections: connections.filter(l => l.type === 'dwell').length,
        connectedDevices: [],
        connectedLocations: [],
      });
    }
    handleCloseContextMenu();
  };

  const handleFilterToEntity = () => {
    if (contextMenu && onEntityFilter) {
      onEntityFilter([contextMenu.node.id]);
    }
    handleCloseContextMenu();
  };

  const handleExpandNode = () => {
    if (contextMenu && onToggleExpand) {
      onToggleExpand(contextMenu.node.id);
    }
    handleCloseContextMenu();
  };

  const handleHideNode = () => {
    if (contextMenu && onToggleHide) {
      onToggleHide(contextMenu.node.id);
    }
    handleCloseContextMenu();
  };

  // Zoom controls
  const handleZoomIn = () => zoomAtCanvasPoint(dimensions.width / 2, dimensions.height / 2, 1.2);
  const handleZoomOut = () => zoomAtCanvasPoint(dimensions.width / 2, dimensions.height / 2, 1 / 1.2);
  const handleZoomReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // DISCOVERY MODE: No seed selected - show entity rankings
  if (selectedEntityIds.length === 0) {
    return (
      <Box ref={containerRef} sx={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        background: isDarkMode 
          ? 'radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d1a 100%)'
          : 'radial-gradient(ellipse at center, #f8fafc 0%, #e2e8f0 100%)',
        overflow: 'hidden',
      }}>
        {/* Animated background rings */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          opacity: 0.15,
          pointerEvents: 'none',
        }}>
          {[1, 2, 3, 4].map(i => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${i * 25}%`,
                height: `${i * 25}%`,
                transform: 'translate(-50%, -50%)',
                border: `2px dashed ${RING_COLORS[i]}`,
                borderRadius: '50%',
                animation: `pulse ${3 + i}s ease-in-out infinite`,
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.3, transform: 'translate(-50%, -50%) scale(1)' },
                  '50%': { opacity: 0.6, transform: 'translate(-50%, -50%) scale(1.02)' },
                },
              }}
            />
          ))}
        </Box>

        {/* Header */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          p: 3,
          background: isDarkMode 
            ? 'linear-gradient(to bottom, rgba(13,13,26,0.9) 0%, transparent 100%)'
            : 'linear-gradient(to bottom, rgba(255,255,255,0.9) 0%, transparent 100%)',
          zIndex: 10,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <TravelExploreIcon sx={{ fontSize: 32, color: DEPTH_COLORS.l1 }} />
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                background: `linear-gradient(135deg, ${DEPTH_COLORS.l1} 0%, ${DEPTH_COLORS.l2} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Network Discovery
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select an entity to explore its connection network
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Connection Type Filter - Top Right */}
        <Box sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
        }}>
          <ToggleButtonGroup
            value={selectedConnectionType}
            exclusive
            onChange={(_, val) => val && setSelectedConnectionType(val)}
            size="small"
            sx={{ 
              bgcolor: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.9)',
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                border: 'none',
                px: 1.5,
                py: 0.5,
              }
            }}
          >
            {connectionTypes.map(ct => (
              <ToggleButton 
                key={ct.value} 
                value={ct.value}
                sx={{ 
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  gap: 0.5,
                  '&.Mui-selected': {
                    bgcolor: ct.value === 'all' ? alpha(DEPTH_COLORS.l1, 0.2) : alpha(typeColorMap[ct.value] || DEPTH_COLORS.l1, 0.2),
                    color: ct.value === 'all' ? DEPTH_COLORS.l1 : typeColorMap[ct.value],
                  }
                }}
              >
                {ct.icon}
                <span>{ct.label}</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Main Content - Entity Matrix */}
        <Box sx={{
          position: 'absolute',
          top: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(900px, 90%)',
          maxHeight: 'calc(100% - 140px)',
          overflow: 'auto',
          zIndex: 5,
        }}>
          {/* Stats Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 3,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Chip 
              icon={<HubIcon />} 
              label={`${nodes.length} nodes`} 
              sx={{ bgcolor: alpha(DEPTH_COLORS.l1, 0.15), color: DEPTH_COLORS.l1, fontWeight: 600 }}
            />
            <Chip 
              icon={<HubIcon />} 
              label={`${filteredLinks.length} connections`} 
              sx={{ bgcolor: alpha(DEPTH_COLORS.l2, 0.15), color: DEPTH_COLORS.l2, fontWeight: 600 }}
            />
            <Chip 
              icon={<PersonSearchIcon />} 
              label={`${entityRankings.length} entities`} 
              sx={{ bgcolor: alpha(DEPTH_COLORS.l3, 0.15), color: DEPTH_COLORS.l3, fontWeight: 600 }}
            />
          </Box>

          {/* Entity Cards Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 2,
            pb: 4,
          }}>
            {entityRankings.map((entity, index) => (
              <Box
                key={entity.id}
                onClick={() => onEntityFilter?.([entity.id])}
                onMouseEnter={() => setHoveredEntity(entity.id)}
                onMouseLeave={() => setHoveredEntity(null)}
                sx={{
                  bgcolor: isDarkMode 
                    ? hoveredEntity === entity.id ? 'rgba(0, 188, 212, 0.15)' : 'rgba(20, 20, 40, 0.8)'
                    : hoveredEntity === entity.id ? 'rgba(0, 188, 212, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 3,
                  p: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: hoveredEntity === entity.id 
                    ? DEPTH_COLORS.l1 
                    : isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  transform: hoveredEntity === entity.id ? 'translateY(-2px)' : 'none',
                  boxShadow: hoveredEntity === entity.id 
                    ? '0 8px 32px rgba(0, 188, 212, 0.3)' 
                    : '0 4px 12px rgba(0,0,0,0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Rank Badge */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 40,
                  height: 40,
                  background: index < 3 
                    ? `linear-gradient(135deg, ${DEPTH_COLORS.seed} 0%, #B8956C 100%)`
                    : `linear-gradient(135deg, ${DEPTH_COLORS.l1} 0%, #293241 100%)`,
                  borderRadius: '0 12px 0 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Typography sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    fontSize: index < 3 ? '1rem' : '0.85rem' 
                  }}>
                    #{index + 1}
                  </Typography>
                </Box>

                {/* Entity Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pr: 4 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: alpha(DEPTH_COLORS.l1, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${DEPTH_COLORS.l1}`,
                  }}>
                    <Typography sx={{ color: DEPTH_COLORS.l1, fontWeight: 700 }}>
                      {entity.label.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600, 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {entity.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entity.type}
                    </Typography>
                  </Box>
                </Box>

                {/* Depth Bars */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Connection Depth</Typography>
                    <Typography variant="caption" sx={{ color: DEPTH_COLORS.l1, fontWeight: 600 }}>
                      {entity.total} total
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, height: 24 }}>
                    {[
                      { key: 'l1', value: entity.l1, color: DEPTH_COLORS.l1, label: 'L1' },
                      { key: 'l2', value: entity.l2, color: DEPTH_COLORS.l2, label: 'L2' },
                      { key: 'l3', value: entity.l3, color: DEPTH_COLORS.l3, label: 'L3' },
                      { key: 'l4', value: entity.l4, color: DEPTH_COLORS.l4, label: 'L4' },
                    ].map(level => (
                      <Tooltip key={level.key} title={`${level.label}: ${level.value} connections`}>
                        <Box sx={{
                          flex: Math.max(level.value, 1),
                          bgcolor: alpha(level.color, level.value > 0 ? 0.8 : 0.1),
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: level.value > 0 ? 30 : 20,
                          transition: 'flex 0.3s ease',
                        }}>
                          {level.value > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: 'white', 
                              fontWeight: 600,
                              fontSize: '0.65rem',
                            }}>
                              {level.value}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                {/* Connection Type Breakdown */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(Object.entries(entity.typeBreakdown) as [string, number][]).map(([type, count]) => (
                    count > 0 && (
                      <Chip
                        key={type}
                        size="small"
                        label={`${type}: ${count}`}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: alpha(typeColorMap[type] || '#888', 0.15),
                          color: typeColorMap[type] || '#888',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    )
                  ))}
                </Box>

                {/* Hover Action Hint */}
                {hoveredEntity === entity.id && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                    <Typography variant="caption" sx={{ color: DEPTH_COLORS.l1 }}>
                      Click to explore
                    </Typography>
                    <TravelExploreIcon sx={{ fontSize: 14, color: DEPTH_COLORS.l1 }} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {entityRankings.length === 0 && (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              color: 'text.secondary',
            }}>
              <HubIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6">No entities found</Typography>
              <Typography variant="body2">
                Try changing the connection type filter or loading data with a different date range
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer Legend */}
        <Box sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 3,
          bgcolor: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.9)',
          borderRadius: 2,
          px: 3,
          py: 1.5,
          zIndex: 10,
        }}>
          {[
            { label: 'L1 - Direct', color: DEPTH_COLORS.l1 },
            { label: 'L2 - 2 hops', color: DEPTH_COLORS.l2 },
            { label: 'L3 - 3 hops', color: DEPTH_COLORS.l3 },
            { label: 'L4 - 4 hops', color: DEPTH_COLORS.l4 },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // INVESTIGATION MODE: Seed selected - show radial graph
  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Toolbar */}
      <Box sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 10,
        display: 'flex',
        gap: 0.5,
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
        borderRadius: 1,
        p: 0.5,
      }}>
        <Tooltip title="Zoom In">
          <IconButton size="small" onClick={handleZoomIn}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton size="small" onClick={handleZoomOut}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset View">
          <IconButton size="small" onClick={handleZoomReset}>
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Labels</Typography>}
          sx={{ mr: 1, ml: 0.5 }}
        />
      </Box>

      {/* Connection Type Filter */}
      <Box sx={{
        position: 'absolute',
        top: 50,
        left: 8,
        zIndex: 10,
        bgcolor: isDarkMode ? 'rgba(20, 20, 40, 0.95)' : 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        p: 1.5,
        border: '1px solid',
        borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        boxShadow: 3,
      }}>
        <Typography variant="caption" sx={{ color: isDarkMode ? '#888' : '#666', display: 'block', mb: 1, fontWeight: 'bold' }}>
          Connection Type
        </Typography>
        <ToggleButtonGroup
          value={selectedConnectionType}
          exclusive
          onChange={(_, val) => val && setSelectedConnectionType(val)}
          size="small"
          orientation="vertical"
          sx={{ width: '100%' }}
        >
          {connectionTypes.map(ct => (
            <ToggleButton 
              key={ct.value} 
              value={ct.value}
              sx={{ 
                justifyContent: 'flex-start',
                px: 1.5,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
                gap: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '&.Mui-selected': {
                  bgcolor: ct.value === 'all' ? 'primary.main' : typeColorMap[ct.value] || 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: ct.value === 'all' ? 'primary.dark' : typeColorMap[ct.value] || 'primary.dark',
                  }
                }
              }}
            >
              {ct.icon}
              <span>{ct.label}</span>
              {ct.value !== 'all' && (
                <Chip 
                  label={connectionTypeCounts[ct.value] || 0} 
                  size="small" 
                  sx={{ 
                    height: 16, 
                    fontSize: '0.6rem',
                    ml: 'auto',
                    bgcolor: selectedConnectionType === ct.value ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  }} 
                />
              )}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Depth Distribution Chart */}
      <Box sx={{
        position: 'absolute',
        bottom: 20,
        left: 8,
        zIndex: 10,
        width: 180,
        bgcolor: isDarkMode ? 'rgba(20, 20, 40, 0.95)' : 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        p: 1.5,
        border: '1px solid',
        borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        boxShadow: 3,
      }}>
        <Typography variant="caption" sx={{ color: isDarkMode ? '#888' : '#666', display: 'block', mb: 1, fontWeight: 'bold' }}>
          Depth Distribution
        </Typography>
        <Box sx={{ height: 100, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={depthDistribution} layout="vertical" margin={{ top: 0, right: 5, left: 5, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis 
                type="category" 
                dataKey="level" 
                tick={{ fontSize: 10, fill: isDarkMode ? '#aaa' : '#666' }}
                width={35}
                axisLine={false}
                tickLine={false}
              />
              <RechartsTooltip
                {...rechartsTooltipProps}
                formatter={(value: number, _name: string, props: any) => [
                  `${value} nodes`,
                  props.payload.level,
                ]}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {depthDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="caption" sx={{ color: isDarkMode ? '#666' : '#999', display: 'block', mt: 0.5, textAlign: 'center' }}>
          {filteredNodes.length} nodes • {filteredLinks.length} links
        </Typography>
      </Box>

      {/* Node size slider */}
      <Box sx={{
        position: 'absolute',
        bottom: 20,
        right: 20,
        zIndex: 10,
        width: 120,
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
        borderRadius: 1,
        p: 1,
      }}>
        <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
          Node Size
        </Typography>
        <Slider
          size="small"
          value={nodeSize}
          onChange={(_, v) => setNodeSize(v as number)}
          min={4}
          max={16}
          sx={{ py: 0.5 }}
        />
      </Box>

      {/* Stats */}
      <Box sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 10,
        display: 'flex',
        gap: 1,
      }}>
        <Chip
          label={`${filteredNodes.length} nodes`}
          size="small"
          sx={{ bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)' }}
        />
        <Chip
          label={`${filteredLinks.length} links`}
          size="small"
          sx={{ bgcolor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)' }}
        />
        {selectedEntityIds.length > 0 && (
          <Chip
            label={`${selectedEntityIds.length} seed${selectedEntityIds.length > 1 ? 's' : ''}`}
            size="small"
            color="warning"
          />
        )}
      </Box>

      {/* Focused Node Info Panel */}
      {focusedNodeId && (() => {
        const focusedNode = filteredNodes.find(n => n.id === focusedNodeId);
        const pathNodeCount = focusedConnections.connectedNodeIds.size;
        const focusedDepth = nodeDepths.get(focusedNodeId);
        const isSeed = selectedEntityIds.includes(focusedNodeId);
        const pathFound = focusedConnections.pathFound;
        if (!focusedNode) return null;
        
        // Determine path description
        let pathDescription = '';
        const connectionTypeName = selectedConnectionType === 'all' ? '' : 
          selectedConnectionType === 'call' ? 'call' :
          selectedConnectionType === 'sms' ? 'SMS' :
          selectedConnectionType === 'browsing' ? 'web' :
          selectedConnectionType === 'app' ? 'app' : selectedConnectionType;
        
        if (isSeed) {
          pathDescription = 'Seed node';
        } else if (!pathFound) {
          pathDescription = `L${focusedDepth ?? '?'} • No ${connectionTypeName} path to seed`;
        } else {
          pathDescription = `L${focusedDepth ?? '?'} • ${pathNodeCount} nodes in path`;
        }
        
        return (
          <Box sx={{
            position: 'absolute',
            top: 50,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            bgcolor: isDarkMode ? 'rgba(0, 50, 70, 0.95)' : 'rgba(230, 250, 255, 0.95)',
            border: '2px solid #00ffff',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#00ffff' }}>
                🔍 {focusedNode.label || focusedNode.id}
              </Typography>
              <Typography variant="caption" sx={{ color: !pathFound && !isSeed ? '#ff9800' : (isDarkMode ? '#aaa' : '#666') }}>
                {pathDescription}
              </Typography>
            </Box>
            <Tooltip title="Clear focus (Esc)">
              <IconButton 
                size="small" 
                onClick={() => setFocusedNodeId(null)}
                sx={{ 
                  color: '#00ffff',
                  '&:hover': { bgcolor: 'rgba(0, 255, 255, 0.2)' }
                }}
              >
                <CenterFocusStrongIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })()}

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          cursor: isDragging ? 'grabbing' : hoveredNode ? 'pointer' : 'grab',
          width: '100%',
          height: '100%',
        }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          setHoveredNode(null);
          setIsDragging(false);
        }}
        onContextMenu={handleContextMenu}
        onWheel={handleWheel}
      />

      {/* Hover tooltip */}
      {hoveredNode && !contextMenu && (
        <Box
          sx={{
            position: 'fixed',
            // Position tooltip near cursor, but keep it within viewport
            left: Math.min(mousePosition.x + 15, window.innerWidth - 300),
            top: Math.min(mousePosition.y + 15, window.innerHeight - 200),
            // If tooltip would go off left/top, flip to other side of cursor
            ...(mousePosition.x > window.innerWidth - 300 && { left: mousePosition.x - 295 }),
            ...(mousePosition.y > window.innerHeight - 200 && { top: mousePosition.y - 180 }),
            pointerEvents: 'none',
            bgcolor: isDarkMode ? 'rgba(30, 30, 50, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            borderRadius: 1,
            p: 1.5,
            maxWidth: 280,
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {hoveredNode.label || hoveredNode.id}
          </Typography>
          {hoveredNode.type && (
            <Chip
              label={hoveredNode.type}
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: typeColorMap[hoveredNode.type] || '#666',
                color: '#fff',
                height: 20,
                fontSize: '0.7rem'
              }}
            />
          )}
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Connections: {nodeDegrees.get(hoveredNode.id) || 0}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Depth: L{nodeDepths.get(hoveredNode.id) ?? '?'}
          </Typography>
          {hoveredNode.place_name && (
            <Typography variant="caption" display="block" color="text.secondary">
              {hoveredNode.place_name}
            </Typography>
          )}
          {!focusedNodeId && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#00bcd4', fontStyle: 'italic' }}>
              Click to highlight connections
            </Typography>
          )}
        </Box>
      )}

      {/* Context menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Node: {contextMenu?.node.label || contextMenu?.node.id}
          </Typography>
          {contextMenu?.node.type && (
            <Chip
              label={contextMenu.node.type}
              size="small"
              sx={{
                ml: 1,
                height: 18,
                fontSize: '0.65rem',
                bgcolor: typeColorMap[contextMenu.node.type] || '#666',
                color: '#fff'
              }}
            />
          )}
        </Box>

        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleFilterToEntity}>
          <ListItemIcon>
            <FilterAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Filter to This Entity</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExpandNode}>
          <ListItemIcon>
            <UnfoldMoreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {expandedNodes.has(contextMenu?.node.id || '') ? 'Collapse' : 'Expand'} Connections
          </ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleHideNode}>
          <ListItemIcon>
            <VisibilityOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Hide Node</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EgoRadialGraph;

